import { createCustomerLookup } from './reportOwnership';

/**
 * Shared segment rule evaluation utilities.
 * Canonical source for applying segment rules to contacts, customers, and opportunities.
 * Used by CampaignBuilder (preview + dynamic counts) and SegmentBuilder (preview).
 */

function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
}

/**
 * Evaluate a single condition against a field value.
 * Handles all operator types including Firestore Timestamp dates.
 */
function evaluateCondition(fieldValue, operator, conditionValue, fieldName) {
  switch (operator) {
    case 'equals':
      if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
        return fieldValue.toLowerCase() === conditionValue.toLowerCase();
      }
      return fieldValue === conditionValue;
    case 'not_equals':
      if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
        return fieldValue.toLowerCase() !== conditionValue.toLowerCase();
      }
      return fieldValue !== conditionValue;
    case 'contains':
      if (Array.isArray(fieldValue)) {
        if (fieldName === 'salesReps' || fieldName === 'customer.salesReps') {
          return fieldValue.some(item =>
            item.salesRepName?.toLowerCase().includes(String(conditionValue || '').toLowerCase())
          );
        }
        return fieldValue.some(item =>
          String(item).toLowerCase().includes(String(conditionValue || '').toLowerCase())
        );
      }
      return String(fieldValue || '').toLowerCase().includes(String(conditionValue || '').toLowerCase());
    case 'not_contains':
      if (Array.isArray(fieldValue)) {
        if (fieldName === 'salesReps' || fieldName === 'customer.salesReps') {
          return !fieldValue.some(item =>
            item.salesRepName?.toLowerCase().includes(String(conditionValue || '').toLowerCase())
          );
        }
        return !fieldValue.some(item =>
          String(item).toLowerCase().includes(String(conditionValue || '').toLowerCase())
        );
      }
      return !String(fieldValue || '').toLowerCase().includes(String(conditionValue || '').toLowerCase());
    case 'greater_than':
      return Number(fieldValue) > Number(conditionValue);
    case 'less_than':
      return Number(fieldValue) < Number(conditionValue);
    case 'after': {
      const targetDate = new Date(conditionValue);
      const fieldDate = fieldValue?.toDate ? fieldValue.toDate() : new Date(fieldValue);
      return fieldDate > targetDate;
    }
    case 'before': {
      const targetDate = new Date(conditionValue);
      const fieldDate = fieldValue?.toDate ? fieldValue.toDate() : new Date(fieldValue);
      return fieldDate < targetDate;
    }
    case 'is_true':
      return fieldValue === true;
    case 'is_false':
      return fieldValue === false;
    case 'is_empty':
      if (Array.isArray(fieldValue)) return fieldValue.length === 0;
      return !fieldValue || fieldValue === '';
    case 'is_not_empty':
      if (Array.isArray(fieldValue)) return fieldValue.length > 0;
      return fieldValue && fieldValue !== '';
    default:
      return false;
  }
}

/**
 * Combine condition results using per-condition logicalOperator (preferred)
 * or top-level rules.operator (legacy fallback).
 */
function combineResults(results, conditions, rules) {
  if (results.length === 0) return false;
  if (results.length === 1) return results[0];

  // Check if any condition uses per-condition logicalOperator
  const hasPerCondition = conditions.some(c => c.logicalOperator);

  if (hasPerCondition) {
    let finalResult = results[0];
    for (let i = 1; i < results.length; i++) {
      const logicalOp = conditions[i].logicalOperator || 'AND';
      if (logicalOp === 'OR') {
        finalResult = finalResult || results[i];
      } else {
        finalResult = finalResult && results[i];
      }
    }
    return finalResult;
  }

  // Legacy: use top-level rules.operator
  if (rules.operator === 'OR') {
    return results.some(r => r);
  }
  return results.every(r => r);
}

/**
 * Apply segment rules to filter an array of items.
 * Works for any entity type (contacts, customers, opportunities).
 */
export function applyRules(items, rules) {
  if (!rules?.conditions?.length) return items;

  return items.filter(item => {
    const results = rules.conditions.map(cond => {
      const fieldValue = getNestedValue(item, cond.field);
      return evaluateCondition(fieldValue, cond.operator, cond.value, cond.field);
    });
    return combineResults(results, rules.conditions, rules);
  });
}

function validateEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

/**
 * Evaluate a segment against live data and return recipients with valid emails.
 *
 * @param {Object} segment - Segment doc with { segmentType, rules }
 * @param {Object} data - { contacts, customers, opportunities } arrays from Firestore
 * @returns {Array} Deduplicated recipients: { contactName, contactEmail, customer }
 */
export function evaluateSegmentRecipients(segment, data) {
  const { contacts = [], customers = [], opportunities = [] } = data;

  if (segment.segmentType === 'opportunities') {
    const filtered = applyRules(opportunities, segment.rules);
    const contactsById = new Map(contacts.map(c => [c.id, c]));

    const recipients = [];
    for (const opp of filtered) {
      let email = opp.contactEmail;
      if (!email && opp.contactId) {
        const rec = contactsById.get(opp.contactId);
        if (rec) email = rec.email || rec.contactEmail || '';
      }
      if (email && validateEmail(email)) {
        recipients.push({
          contactName: opp.contactName || opp.customerName || '',
          contactEmail: email,
          customer: { customerName: opp.customerName || '' }
        });
      }
    }
    return recipients;

  } else if (segment.segmentType === 'contacts') {
    // Enrich contacts with customer data
    const lookup = createCustomerLookup(customers);

    const enriched = contacts.map(c => {
      const cust = lookup.forAccount(c.customerId) || {};
      const fn = c.firstName?.trim() || c.contactFirstName?.trim() || '';
      const ln = c.lastName?.trim() || c.contactLastName?.trim() || '';
      return {
        ...c,
        customer: cust,
        contactName: fn && ln ? `${fn} ${ln}` : fn || ln || 'Unnamed',
        contactTitle: c.contactPosition || c.title || '',
        contactEmail: c.email || c.contactEmail || '',
        contactPhone: c.phone || c.contactPhone || '',
        contactFocus: c.contactFocus || [],
        contactPrimary: (c.isPrimary === true || c.isPrimary === 1 || c.isPrimary === '1' || c.contactIsMainContact === true || c.contactIsMainContact === 1 || c.contactIsMainContact === '1') ? 'Yes' : 'No'
      };
    });

    const filtered = applyRules(enriched, segment.rules);
    return filtered.filter(c => c.contactEmail && validateEmail(c.contactEmail));

  } else if (segment.segmentType === 'all') {
    // Cross-collection: contacts as base, enriched with customer + opportunity data
    const lookup = createCustomerLookup(customers);

    // Build reverse lookup: contact → opportunities
    const oppsByContactEmail = new Map();
    const oppsByContactId = new Map();
    opportunities.forEach(opp => {
      if (opp.contactEmail) {
        const key = opp.contactEmail.toLowerCase();
        if (!oppsByContactEmail.has(key)) oppsByContactEmail.set(key, []);
        oppsByContactEmail.get(key).push(opp);
      }
      if (opp.contactId) {
        if (!oppsByContactId.has(opp.contactId)) oppsByContactId.set(opp.contactId, []);
        oppsByContactId.get(opp.contactId).push(opp);
      }
    });

    const enriched = contacts.map(c => {
      const cust = lookup.forAccount(c.customerId) || {};
      const fn = c.firstName?.trim() || c.contactFirstName?.trim() || '';
      const ln = c.lastName?.trim() || c.contactLastName?.trim() || '';
      return {
        ...c,
        customer: cust,
        contactName: fn && ln ? `${fn} ${ln}` : fn || ln || 'Unnamed',
        contactTitle: c.contactPosition || c.title || '',
        contactEmail: c.email || c.contactEmail || '',
        contactPhone: c.phone || c.contactPhone || '',
        contactFocus: c.contactFocus || [],
        contactPrimary: (c.isPrimary === true || c.isPrimary === 1 || c.isPrimary === '1' || c.contactIsMainContact === true || c.contactIsMainContact === 1 || c.contactIsMainContact === '1') ? 'Yes' : 'No'
      };
    });

    const recipients = [];
    for (const contact of enriched) {
      if (!contact.contactEmail || !validateEmail(contact.contactEmail)) continue;

      const customer = contact.customer || {};
      const byEmail = oppsByContactEmail.get(contact.contactEmail.toLowerCase()) || [];
      const byId = oppsByContactId.get(contact.id) || [];
      const seenIds = new Set();
      const linkedOpps = [...byEmail, ...byId].filter(o => {
        if (seenIds.has(o.id)) return false;
        seenIds.add(o.id);
        return true;
      });

      const results = segment.rules.conditions.map(condition => {
        const sepIdx = condition.field.indexOf('__');
        if (sepIdx === -1) return false;
        const source = condition.field.substring(0, sepIdx);
        const actualField = condition.field.substring(sepIdx + 2);

        if (source === 'customer') {
          return evaluateCondition(getNestedValue(customer, actualField), condition.operator, condition.value, actualField);
        } else if (source === 'contact') {
          return evaluateCondition(getNestedValue(contact, actualField), condition.operator, condition.value, actualField);
        } else if (source === 'opp') {
          if (linkedOpps.length === 0) return condition.operator === 'is_empty';
          if (condition.operator === 'is_empty') {
            return linkedOpps.every(o => {
              const fv = getNestedValue(o, actualField);
              return !fv || (Array.isArray(fv) && fv.length === 0);
            });
          }
          if (condition.operator === 'is_not_empty') {
            return linkedOpps.some(o => {
              const fv = getNestedValue(o, actualField);
              return fv && (!Array.isArray(fv) || fv.length > 0);
            });
          }
          return linkedOpps.some(o => evaluateCondition(getNestedValue(o, actualField), condition.operator, condition.value, actualField));
        }
        return false;
      });

      if (combineResults(results, segment.rules.conditions, segment.rules)) {
        recipients.push({
          contactName: contact.contactName,
          contactEmail: contact.contactEmail,
          customer: { customerName: customer.customerName || '' }
        });
      }
    }
    return recipients;

  } else {
    // Customer segment
    const filtered = applyRules(customers, segment.rules);
    const contactsByCustomerId = new Map();
    contacts.forEach(c => {
      const key = String(c.customerId);
      if (!contactsByCustomerId.has(key)) contactsByCustomerId.set(key, []);
      contactsByCustomerId.get(key).push(c);
    });

    const recipients = [];
    for (const customer of filtered) {
      const custContacts = contactsByCustomerId.get(String(customer.customerAccountNumber || customer.id)) || [];
      if (custContacts.length > 0) {
        for (const c of custContacts) {
          const email = c.email || c.contactEmail;
          if (email && validateEmail(email)) {
            const fn = c.firstName?.trim() || c.contactFirstName?.trim() || '';
            const ln = c.lastName?.trim() || c.contactLastName?.trim() || '';
            recipients.push({
              contactName: fn && ln ? `${fn} ${ln}` : fn || ln || 'Unnamed',
              contactEmail: email,
              customer: customer
            });
          }
        }
      } else if (customer.customerEmail && validateEmail(customer.customerEmail)) {
        recipients.push({
          contactName: customer.customerName || '',
          contactEmail: customer.customerEmail,
          customer: customer,
          isFallback: true
        });
      }
    }
    return recipients;
  }
}

/**
 * Evaluate multiple segments and return deduplicated recipients.
 *
 * @param {Array} selectedSegments - Array of segment docs
 * @param {Object} data - { contacts, customers, opportunities }
 * @returns {{ recipients: Array, perSegmentCounts: Object, totalCount: number }}
 */
export function evaluateAllSegments(selectedSegments, data) {
  const allRecipients = [];
  const perSegmentCounts = {};

  for (const segment of selectedSegments) {
    const segRecipients = evaluateSegmentRecipients(segment, data);
    // Dedup within each segment by email so per-segment count matches the total
    const uniqueInSegment = Array.from(
      new Map(segRecipients.map(r => [r.contactEmail.toLowerCase(), r])).values()
    );
    perSegmentCounts[segment.id] = uniqueInSegment.length;
    allRecipients.push(...uniqueInSegment);
  }

  // Deduplicate across segments by email
  const unique = Array.from(
    new Map(allRecipients.map(r => [r.contactEmail.toLowerCase(), r])).values()
  );

  return {
    recipients: unique,
    perSegmentCounts,
    totalCount: unique.length
  };
}
